/**
 * Single source of truth for formatting currency, cost and quantity values
 * in nl-BE notation. Replaces the ad-hoc `currency` Vue filter (main.ts) and
 * the Intl-based `formatBelgianCurrency` helper (currencyHelper.ts).
 */

// `as const` is niet cosmetisch: DevExtreme typeert `format.type` als een
// literal union ('currency' | 'fixedPoint' | 'percent' | ...). Zonder
// `as const` leidt TypeScript hier `string` af en faalt elke kolom die deze
// constante aan een getypeerde `format`-property toekent onder strict mode.
export const CURRENCY_FORMAT = { type: 'currency', precision: 2 } as const;
export const CURRENCY_FORMAT_COST = { type: 'currency', precision: 4 } as const;
export const FIXED_POINT_FORMAT = { type: 'fixedPoint', precision: 2 } as const;
export const QUANTITY_FORMAT = { type: 'fixedPoint', precision: 3 } as const;
export const PERCENT_FORMAT = { type: 'percent', precision: 2 } as const;

/**
 * Rounds `value` to `precision` decimals, half-away-from-zero, without
 * suffering from binary floating-point drift (e.g. 1.005 -> 1.01, not 1.00).
 *
 * The trick: build the numeric literal `"<abs>e<precision>"` and let the
 * JS number parser interpret it directly as a decimal value. Because the
 * parser reads the *decimal* string (the shortest round-trip representation
 * of the stored double), it lands on the intended value (e.g. 100.5)
 * instead of replaying the multiplication `1.005 * 100`, which would
 * inherit 1.005's own binary rounding error.
 *
 * Math.round() rounds half towards +Infinity, not away from zero, so we
 * operate on the absolute value and re-apply the sign afterwards to get
 * true half-away-from-zero rounding for negative numbers too.
 */
export function roundHalfAwayFromZero(value: number, precision: number): number {
  if (!isFinite(value)) {
    return value;
  }

  const sign = value < 0 ? -1 : 1;
  const abs = Math.abs(value);

  const shifted = Number(`${abs}e${precision}`);
  const rounded = Math.round(shifted);
  const magnitude = Number(`${rounded}e-${precision}`);

  const signed = sign * magnitude;

  // Normalise -0 to 0 (a negative value that rounds to zero, e.g. -0.001,
  // must not be displayed as "-0,00"). `-0 === 0` is true in JS, so this
  // swaps a negative zero for a literal positive zero without touching
  // any other value.
  return signed === 0 ? 0 : signed;
}

function toNlBe(value: number, precision: number): string {
  return value.toLocaleString('nl-BE', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
}

function format(value: number | null | undefined, precision: number, prefix: string): string {
  // The legacy `currency` filter used a falsy check (`!value && value !== 0`),
  // which silently swallowed NaN and Infinity. Division-derived values
  // (percentages, margins) can still reach the UI as NaN, so keep returning
  // an empty string for anything that is not a finite number.
  if (value === null || value === undefined || !isFinite(value)) {
    return '';
  }

  const rounded = roundHalfAwayFromZero(value, precision);

  return prefix + toNlBe(rounded, precision);
}

/**
 * Formats a monetary amount in nl-BE notation with a "€ " prefix, 2
 * decimals, half-away-from-zero rounding. Mirrors the legacy `currency`
 * Vue filter exactly: '' for null/undefined, '€ 0,00' for 0.
 */
export function formatCurrency(value?: number | null): string {
  return format(value, CURRENCY_FORMAT.precision, '€ ');
}

/**
 * Formats a cost amount in nl-BE notation with a "€ " prefix and 4
 * decimals (costs are tracked with more precision than customer-facing
 * currency amounts).
 */
export function formatCost(value?: number | null): string {
  return format(value, CURRENCY_FORMAT_COST.precision, '€ ');
}

/**
 * Formats a quantity in nl-BE notation, 3 decimals, no currency symbol.
 */
export function formatQuantity(value?: number | null): string {
  return format(value, QUANTITY_FORMAT.precision, '');
}
